import React, {
    type ChangeEvent,
    type FocusEvent,
    forwardRef,
    type RefObject,
    useEffect,
    useRef,
    useState,
} from "react";
import { getCounterValue } from "./counter.js";
import type { BaseTextAreaProps } from "./types.js";

export const BaseTextArea = forwardRef<HTMLTextAreaElement, BaseTextAreaProps>(
    (props, ref) => {
        const {
            autoExpand,
            counter,
            defaultValue,
            onBlur,
            onFocus,
            rows = 7,
            placeholder = " ", // This space intentionally left blank. Denne + rows trengs for å få den ekspanderende effekten.
            startOpen,
            style,
            value,
            "aria-invalid": ariaInvalid,
            onChange,
            ...rest
        } = props;

        const strategy = counter?.strategy ?? "characters";
        const isControlled = typeof value !== "undefined";

        const [uncontrolledValue, setUncontrolledValue] =
            useState(defaultValue);
        const [textAreaFocused, setTextAreaFocused] = useState(false);
        const internalRef = useRef<HTMLTextAreaElement>(null);
        const textAreaRef =
            (ref as RefObject<HTMLTextAreaElement>) || internalRef;

        // Hvis feltet styres utenfra bruker vi `value`, ellers holder vi styr på verdien selv.
        const textAreaValue = isControlled ? value : uncontrolledValue;
        const textAreaValueProps = isControlled ? { value } : { defaultValue };

        const counterCurrent = getCounterValue(textAreaValue, strategy);
        const counterTotal: number = counter?.maxLength || 0;
        const progressCurrent: number = counterTotal - counterCurrent;
        const isOverLimit = Boolean(counter && counterCurrent > counterTotal);
        const invalid = Boolean(ariaInvalid || isOverLimit);

        // biome-ignore lint/correctness/useExhaustiveDependencies: counterCurrent trengs for å lytte på tekstendringer i textarea for auto-expand funksjonalitet
        useEffect(() => {
            const textAreaElement = textAreaRef.current;
            if (textAreaElement) {
                if (!autoExpand) {
                    textAreaElement.style.height = "";
                    return;
                }

                if (textAreaFocused || textAreaValue) {
                    textAreaElement.style.height = "auto"; // Sett til auto før scrollhøyden leses, sånn at redusering av høyde ved sletting av tekst fungerer
                    textAreaElement.style.height = `${textAreaElement.scrollHeight}px`;
                } else {
                    textAreaElement.style.height = "";
                }
            }
        }, [
            autoExpand,
            textAreaRef,
            textAreaValue,
            textAreaFocused,
            counterCurrent,
        ]);

        function handleOnFocus(e: FocusEvent<HTMLTextAreaElement>) {
            setTextAreaFocused(true);
            if (onFocus) {
                onFocus(e);
            }
        }

        function handleOnBlur(e: FocusEvent<HTMLTextAreaElement>) {
            setTextAreaFocused(false);
            if (onBlur) {
                onBlur(e);
            }
        }

        function handleOnChange(e: ChangeEvent<HTMLTextAreaElement>) {
            if (!isControlled) {
                setUncontrolledValue(e.target.value);
            }

            if (onChange) {
                onChange(e);
            }
        }
        function calculatePercentage(current: number, total: number): number {
            if (current <= 0) {
                return 0;
            }
            return total === 0 ? 0 : (current * 100) / total;
        }

        const overflowStyle = {
            overflowX: autoExpand ? "hidden" : undefined, // Must set overflowX hidden for Firefox https://stackoverflow.com/a/22700700
        } as React.CSSProperties;

        return (
            <div
                className="jkl-text-area-wrapper"
                data-invalid={invalid}
                data-has-content={counterCurrent > 0}
            >
                <textarea
                    aria-invalid={invalid}
                    className={`jkl-text-area__text-area jkl-text-area__text-area--${rows}-rows`}
                    onBlur={handleOnBlur}
                    onFocus={handleOnFocus}
                    onChange={handleOnChange}
                    ref={textAreaRef}
                    style={{ ...style, ...overflowStyle }}
                    placeholder={placeholder}
                    {...textAreaValueProps}
                    {...rest}
                />
                {counter && (
                    <div className="jkl-text-area__counter" aria-hidden="true">
                        <div className="jkl-text-area__counter-count">
                            {counterCurrent}&nbsp;/&nbsp;{counterTotal}
                        </div>
                        {!counter.hideProgress && (
                            <div
                                className="jkl-text-area__counter-progress"
                                style={{
                                    ["--progress-width" as string]: `${calculatePercentage(
                                        progressCurrent,
                                        counterTotal,
                                    )}%`,
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    },
);
BaseTextArea.displayName = "BaseTextArea";
